import {useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';

import useAuth from '../../../module/auth/useAuth';
import {useAppDispatch, useAppSelector} from '../../../module/redux/hooks';
import {genAnalysisDataset, getPlotColor} from '../../../service/plotUtils';
import {FeatureName} from '../../../enums';
import {actions, init} from './slice';
import Panel from '../../Panel';
import ToggleGroup from '../../ToggleGroup';
import DateRangePicker from '../../DateRangePicker';
import Plot from '../../Plot';


interface Props {
    feature: CategoryFeature;
    colorIndex?: number;
}

interface DefStandard {
    ref: [number, number];
    gender?: 'male' | 'female';
    age_min?: number;
    age_max?: number;
}

const STANDARDS: {[key: string]: DefStandard[]} = {
    [FeatureName.Cholesterol]: [{ref: [3, 7]}],
    [FeatureName.Glucose]:     [{ref: [4, 5.6]}],
    [FeatureName.Testosteron]: [
        {gender: 'male',   ref: [10, 35]},
        {gender: 'female', ref: [.5, 2.4]}
    ],
    [FeatureName.TSH]:         [{ref: [.3, 4.2]}],
    [FeatureName.T4Free]:      [{ref: [10.3, 22]}]
};

const getStandard = (user: FixType, standards: CategoryFeatureStandard[], defStandard: DefStandard[]): [number, number]|undefined => {
    const userAge = dayjs().year() - user.dob_year;
    if (user && standards.length) {
        const s = standards.find((i) => i.gender === user.gender && (i.age_min <= userAge && userAge <= i.age_max));

        if (s?.value_min && s.value_max) return [s.value_min, s.value_max];
    }

    if (defStandard) {
        return defStandard.find(i =>
            (!i.gender || i.gender === user.gender) &&
            (!i.age_min || i.age_min <= userAge) &&
            (!i.age_max || i.age_max >= userAge)
        )?.ref;
    }

    return undefined;
};

const AnalysisPlot = ({feature, colorIndex}: Props) => {
    const {auth} = useAuth();
    const state = useAppSelector((state) => state.analysisWidget);

    const [data, layout] = useMemo(() => {
        return genAnalysisDataset(
            feature, state.data,
            state.from, state.to,
            getPlotColor(colorIndex),
            getStandard(auth?.user, feature.standards, STANDARDS[feature.name])
        );
    }, [state.data]);

    useEffect(() => {
        const color = getPlotColor(colorIndex);

        data.forEach(d => {
            if (d.marker) d.marker.color = `rgba(${color}, .7)`;
            if (d.line) d.line.color = `rgb(${color})`;
        });
    }, [colorIndex, data]);

    return <Plot data={data} layout={layout} />;
};

const AnalysisWidget = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const state = useAppSelector((state) => state.analysisWidget);

    const onChangeRange = (from: string, to: string) => {
        dispatch(actions.setDates({from, to}));
    };

    useEffect(() => {
        dispatch(init(state.from, state.to));
    }, [state.from, state.to]);

    useEffect(() => {
        dispatch(actions.setFilledFeatures());
    }, [state.data, state.features]);

    let colorIndex = -1;

    return <Panel
        title={t('analysis')}
        loading={state.loading}
        actionsAsList={true}
        actions={<DateRangePicker type={'date'} range={[state.from, state.to]}
            onChangeRange={onChangeRange} quickRangeButtons={['month', 'six_months', 'year']}
        />}
    >
        <ToggleGroup
            onChange={(id) => dispatch(actions.toggleFeature(id))}
            options={state.features.map((f) => ({
                name: t(f.name),
                value: f.id,
                active: state.activeFeatures[f.id],
                filled: state.filledFeatures[f.id],
            }))}
        />

        {state.features.map((f, i) => {
            if (i === 0) colorIndex = -1;
            if (!state.activeFeatures[f.id] || !state.filledFeatures[f.id]) return null;
            colorIndex++;
            return <AnalysisPlot key={i} feature={f} colorIndex={colorIndex}/>;
        })}
    </Panel>;
};

export default AnalysisWidget;
